/*
 * Wishlist Drawer Actions — quick-add slide-up + remove/undo + clear-all.
 * Region/empty state is owned by assets/wishlist-render.js (single writer).
 * Sibling to wishlist-drawer.js (kept separate so each file ≤ 150 lines).
 * Exposes window.BroraWishlistDrawerActions for the lifecycle file.
 */
;(function () {

  // Ajax routes need the market root, or Shopify answers in the default market. routes.root ends in '/'.
  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  'use strict';

  // Latest wishlist items, cached from the render event so quick-add builds its pills synchronously
  // from item.product.variants.
  var latestItems = [];
  document.addEventListener('brora:wishlist:changed', function (e) {
    if (e && e.detail && Array.isArray(e.detail.items)) latestItems = e.detail.items;
  });
  function findItem(card) {
    var itemId = card.getAttribute('data-wishlist-drawer-item-id');
    var pid = card.getAttribute('data-wishlist-drawer-product-id');
    var vid = card.getAttribute('data-wishlist-drawer-variant-id');
    return latestItems.filter(function (it) {
      return it && ((itemId && String(it.id) === String(itemId)) ||
                    (vid && String(it.variant_id) === String(vid)) ||
                    (pid && String(it.product_id) === String(pid)));
    })[0] || null;
  }
  // Envelope and strike cloned from the global size template, built from the same snippets as the
  // Liquid pills.
  function appendUnavailableParts(label) {
    var parts = document.querySelector('[data-tmpl-card-size]')?.content.querySelectorAll('.action-size__icon, .action-size__line');
    parts?.forEach(function (part) {
      var node = label.appendChild(part.cloneNode(true));
      node.removeAttribute('data-slot');
      node.querySelectorAll('[data-slot]').forEach(function (el) { el.removeAttribute('data-slot'); });
    });
  }

  // Per-handle payload cache for the page session. The promise is shared, so one response serves both
  // the pill appearance and the size-pick route; a failure drops out so a later open retries.
  var productRequests = new Map();
  function requestProduct(handle) {
    if (!handle) return Promise.resolve(null);
    var cached = productRequests.get(handle);
    if (cached) return cached;
    var ctrl = new AbortController();
    var timer = setTimeout(function () { ctrl.abort(); }, 8000);
    var request = fetch(shopRoot() + 'products/' + encodeURIComponent(handle) + '.js', { signal: ctrl.signal })
      .then(function (r) { clearTimeout(timer); if (!r.ok) throw new Error('fetch_failed'); return r.json(); })
      .catch(function () { clearTimeout(timer); productRequests.delete(handle); return null; });
    productRequests.set(handle, request);
    return request;
  }
  function handleOf(card, item) {
    return (item && item.product && item.product.handle) ||
      (card && card.getAttribute('data-wishlist-drawer-product-handle')) || '';
  }

  // A pre-order variant is offered here only while one of its selling plans is valid in this market;
  // a variant with no plans, or a page without the market module, is unconstrained.
  function plansAllowedHere(plans) {
    return !plans || !plans.length || !window.BroraPreorderMarket || window.BroraPreorderMarket.anyAllowed(plans);
  }
  function purchasableHere(v) {
    return !!(v && v.available) && plansAllowedHere((v && v.selling_plan_allocations) || []);
  }

  function build(container, announcer) {
    const quickAdd = container.querySelector('[data-wishlist-drawer-quick-add]');
    const quickAddOverlay = container.querySelector('[data-wishlist-drawer-quick-add-overlay]');
    const quickAddPills = container.querySelector('[data-wishlist-drawer-quick-add-pills]');
    const cardsRegion = container.querySelector('[data-wishlist-drawer-cards]');
    const REMOVE_UNDO_MS = 3000;
    const pendingRemovals = new Map();
    let activeCard = null;
    let activeRequest = null;
    let openToken = 0;

    function announce(t) { if (announcer) announcer.textContent = t; }

    if (cardsRegion) {
      const warmFromEvent = function (e) {
        const trigger = e.target.closest && e.target.closest('[data-wishlist-drawer-move-to-bag]');
        if (trigger) warmProduct(trigger.closest('[data-wishlist-drawer-card]'));
      };
      cardsRegion.addEventListener('pointerover', warmFromEvent);
      cardsRegion.addEventListener('focusin', warmFromEvent);
      document.addEventListener('brora:wishlist:changed', function () {
        requestAnimationFrame(warmVisibleCards);
      });
      warmVisibleCards();
    }

    // Build the size pills from the wishlist item's own product data (variants + options), so the
    // list is complete the moment the panel opens. Purchasable sizes are add-to-bag radios; the rest
    // stay operable radios carrying the unavailable envelope and strike, and route to the PDP.
    function buildPills(product) {
      quickAddPills.innerHTML = '';
      const variants = product && product.variants;
      if (!variants || !variants.length) return;
      // has_only_default_variant equivalent (not exposed by the product JSON): a product with no
      // real options has a single "Title" option whose only value is "Default Title".
      const defaultOnly = product.options && product.options.length === 1 &&
        ((product.options[0].name || product.options[0]) === 'Title');
      variants.forEach(function (v) {
        const label = document.createElement('label'); label.className = 'action-size';
        const inp = document.createElement('input');
        inp.type = 'radio'; inp.name = 'wishlist-quick-add-size'; inp.value = String(v.id);
        inp.setAttribute('data-wishlist-drawer-size', '');
        const span = document.createElement('span'); span.className = 'action-size__text';
        span.textContent = defaultOnly ? 'One Size' : v.option1;
        label.appendChild(inp); label.appendChild(span);
        if (!purchasableHere(v)) {
          // Operable, not disabled: the size routes to the PDP instead of adding. The class draws the
          // same greyed strike aria-disabled did, so the pill is unchanged visually.
          label.classList.add('action-size--unavailable');
          appendUnavailableParts(label);
        }
        quickAddPills.appendChild(label);
      });
    }

    // The cached payload carries no selling-plan data, so a market-excluded size starts as an ordinary
    // pill and takes the unavailable state when the live payload lands. Live data only ever withdraws
    // purchasability, so an already-unavailable pill is left alone.
    function gatePills(live) {
      const plans = {};
      (((live || {}).variants) || []).forEach(function (v) { plans[String(v.id)] = v.selling_plan_allocations || []; });
      quickAddPills.querySelectorAll('[data-wishlist-drawer-size]').forEach(function (inp) {
        const label = inp.parentNode;
        // The class marks an already-unavailable pill, guarding against a second envelope and strike.
        if (label.classList.contains('action-size--unavailable') || plansAllowedHere(plans[inp.value])) return;
        label.classList.add('action-size--unavailable');
        appendUnavailableParts(label);
      });
    }

    function openQuickAdd(card) {
      if (!card || !quickAdd || !quickAddOverlay) return;
      activeCard = card;
      const item = findItem(card);
      const token = ++openToken;
      // The same response gates the pills and feeds pickSize. The token discards one that lands after
      // the shopper has opened a different item.
      activeRequest = requestProduct(handleOf(card, item));
      buildPills(item && item.product);
      quickAddOverlay.setAttribute('data-state', 'visible');
      quickAdd.setAttribute('data-state', 'visible');
      activeRequest.then(function (live) { if (token === openToken) gatePills(live); });
    }

    // The lookup starts before the panel is asked for — on cards in view, and on the card a pointer or
    // the keyboard reaches — so the pills open in their final state.
    function warmProduct(card) {
      if (!card) return;
      const handle = handleOf(card, findItem(card));
      if (handle) requestProduct(handle);
    }

    function warmVisibleCards() {
      if (!cardsRegion || typeof IntersectionObserver !== 'function') return;
      const io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          warmProduct(entry.target);
          io.unobserve(entry.target);
        });
      }, { root: cardsRegion });
      cardsRegion.querySelectorAll('[data-wishlist-drawer-card]').forEach(function (card) { io.observe(card); });
    }

    function closeQuickAdd() {
      if (!quickAdd || !quickAddOverlay) return;
      quickAddOverlay.setAttribute('data-state', 'hidden');
      quickAdd.setAttribute('data-state', 'hidden');
      activeCard = null;
    }

    function resolveItemId(card) {
      if (!card) return Promise.resolve(null);
      const direct = card.getAttribute('data-wishlist-drawer-item-id');
      if (direct) return Promise.resolve(direct);
      const pid = card.getAttribute('data-wishlist-drawer-product-id');
      const vid = card.getAttribute('data-wishlist-drawer-variant-id');
      if (!window.BroraWishlist || !window.BroraWishlist.getItems) return Promise.resolve(null);
      return window.BroraWishlist.getItems().then(function (items) {
        const match = (items || []).find(function (it) {
          if (!it) return false;
          if (vid && String(it.variant_id) === String(vid)) return true;
          if (pid && String(it.product_id) === String(pid)) return true;
          return false;
        });
        return match && match.id != null ? match.id : null;
      }).catch(function () { return null; });
    }

    // Pre-order can only be completed on the PDP, where the selling plan rides on the product form.
    // The variant is read from the payload requested when the panel opened — the same response that
    // gated the pills — and any variant carrying a selling-plan allocation is routed to the PDP
    // instead of added. The question the route asks is whether the variant is pre-order at all, not
    // whether its plan is offered in this market: both belong on the PDP, which either completes the
    // pre-order or serves the notify form. A missing handle, a missing variant or any fetch failure
    // takes the ordinary add path: a transient failure must never block a purchase.
    // Already on THIS product's PDP: close the drawer and select the variant there rather than
    // navigating, as the mini-bag does. The close control is the seam the empty state already uses.
    function routeToPdp(handle, variantId) {
      const onThisPdp =
        document.querySelector('[data-main-product]') &&
        handle &&
        window.location.pathname.replace(/\/$/, '').endsWith('/products/' + handle) &&
        window.broraProduct && window.broraProduct.selectVariant;
      if (onThisPdp) {
        closeQuickAdd();
        const closer = container.querySelector('[data-wishlist-drawer-close]');
        if (closer) closer.click();
        // After the focus-trap returns focus, so selectVariant's notify-email focus takes effect.
        requestAnimationFrame(function () { window.broraProduct.selectVariant(variantId, true); });
        return;
      }
      window.location.href = shopRoot() + 'products/' + encodeURIComponent(handle) + '?variant=' + encodeURIComponent(variantId);
    }

    // Neither an out-of-stock nor a pre-order size can be completed here, so both route to the PDP.
    // Stock comes from the item's own product data; pre-order from the live payload.
    function pickSize(variantId) {
      const card = activeCard;
      const item = findItem(card);
      const handle = handleOf(card, item);
      const saved = (((item || {}).product || {}).variants || []).filter(function (x) { return String(x.id) === String(variantId); })[0];
      if (handle && saved && !purchasableHere(saved)) {
        routeToPdp(handle, variantId);
        return;
      }
      (activeRequest || Promise.resolve(null)).then(function (live) {
        const v = (((live || {}).variants) || []).filter(function (x) { return String(x.id) === String(variantId); })[0];
        const allocations = v && v.selling_plan_allocations;
        if (handle && allocations && allocations.length > 0) {
          routeToPdp(handle, variantId);
          return;
        }
        addToBag(variantId, card);
      });
    }

    function addToBag(variantId, card) {
      const ctrl = new AbortController();
      const timer = setTimeout(function () { ctrl.abort(); }, 8000);
      fetch(shopRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }), signal: ctrl.signal
      }).then(function (r) { clearTimeout(timer); if (!r.ok) throw new Error('add_failed'); return r.json(); })
        .then(function () {
          // Refresh the mini-bag count directly; dispatching cart:added would also
          // trigger the ATB notification toast, which is unwanted for move-to-bag.
          if (window.miniBag && typeof window.miniBag.refreshCart === 'function') window.miniBag.refreshCart();
          closeQuickAdd();
          return resolveItemId(card);
        })
        .then(function (itemId) {
          if (itemId && window.BroraWishlist) return window.BroraWishlist.removeItem(itemId);
        })
        .catch(function () { clearTimeout(timer); closeQuickAdd(); });
    }

    function toggleRemoved(card, removed) {
      if (!card) return;
      if (removed) {
        card.setAttribute('data-wishlist-drawer-state', 'removed');
        announce(card.getAttribute('data-item-removed-message') || '');
        const timer = setTimeout(function () {
          resolveItemId(card).then(function (itemId) {
            if (itemId && window.BroraWishlist) return window.BroraWishlist.removeItem(itemId);
          }).catch(function () {});
          card.style.transition = 'opacity 0.3s var(--ease-both)';
          card.style.opacity = '0';
          setTimeout(function () { card.remove(); pendingRemovals.delete(card); }, 300);
        }, REMOVE_UNDO_MS);
        pendingRemovals.set(card, timer);
      } else {
        const t = pendingRemovals.get(card);
        if (t) { clearTimeout(t); pendingRemovals.delete(card); }
        card.setAttribute('data-wishlist-drawer-state', 'default');
        card.style.transition = ''; card.style.opacity = ''; announce('');
      }
    }

    let clearAllTimer = null;

    function clearAll() {
      closeQuickAdd();
      const cards = cardsRegion ? cardsRegion.querySelectorAll('[data-wishlist-drawer-card]') : [];
      if (!cards.length) return;
      container.setAttribute('data-wishlist-drawer-clearing', 'true');
      cards.forEach(function (card) { toggleRemoved(card, true); });
      if (clearAllTimer) clearTimeout(clearAllTimer);
      clearAllTimer = setTimeout(function () {
        container.removeAttribute('data-wishlist-drawer-clearing');
        clearAllTimer = null;
      }, REMOVE_UNDO_MS);
    }

    function clearAllUndo() {
      const cards = cardsRegion ? cardsRegion.querySelectorAll('[data-wishlist-drawer-card][data-wishlist-drawer-state="removed"]') : [];
      if (clearAllTimer) { clearTimeout(clearAllTimer); clearAllTimer = null; }
      container.removeAttribute('data-wishlist-drawer-clearing');
      if (!cards.length) return;
      cards.forEach(function (card) { toggleRemoved(card, false); });
    }

    function openShare(triggerEl) {
      if (window.BroraWishlistDrawerShare && typeof window.BroraWishlistDrawerShare.open === 'function') {
        window.BroraWishlistDrawerShare.open(triggerEl);
        return;
      }
      container.setAttribute('data-wishlist-drawer-share', 'open');
    }

    function closeShare() {
      if (window.BroraWishlistDrawerShare && typeof window.BroraWishlistDrawerShare.close === 'function') {
        window.BroraWishlistDrawerShare.close();
        return;
      }
      container.setAttribute('data-wishlist-drawer-share', 'closed');
    }

    function teardown() {
      pendingRemovals.forEach(function (t) { clearTimeout(t); });
      pendingRemovals.clear();
    }

    return { openQuickAdd: openQuickAdd, closeQuickAdd: closeQuickAdd,
             pickSize: pickSize, toggleRemoved: toggleRemoved, clearAll: clearAll,
             clearAllUndo: clearAllUndo, openShare: openShare, closeShare: closeShare,
             teardown: teardown };
  }

  window.BroraWishlistDrawerActions = { build: build };
})();
